import {ofType} from 'redux-observable';
import {Observable} from 'rxjs';
import {filter, flatMap} from 'rxjs/operators';
import {actionTypes, change} from 'redux-form';

import {combineEpics} from 'web-app/util/redux-observable';
import * as Api from 'web-app/react/entities/bill-payers/api';
import {
    createOrUpdateObservable,
    discontinueBillPayerObservable,
    reviveBillPayerObservable,
    addBillPayerToChildObservable,
} from 'web-app/react/entities/bill-payers/epics';
import * as BillPayerHelpers from 'web-app/react/entities/bill-payers/helpers';
import * as ChildActions from 'web-app/react/entities/children/actions';
import {hasValue} from 'web-app/util/typescript';
import * as BillPayerActions from 'web-app/react/entities/bill-payers/actions';
import * as BillPayerFormConstants from 'web-app/react/components/bill-payer-form/constants';

import * as Actions from './actions';
import * as Constants from './constants';

const localAddBillPayerToChildObservable = (
    institutionId,
    childId,
    existingChildBillPayers,
    existingChildSplitConfiguration,
    newBillPayerData,
    sensitiveInformation,
) => {
    return createOrUpdateObservable(institutionId, childId, newBillPayerData).map(successAction => {
        const newSplitConfiguration = BillPayerHelpers.addBillPayerToSplitConfiguration(
            existingChildSplitConfiguration,
            successAction.payload.billPayer,
        );

        return [
            successAction,
            Actions.addBillPayerToChildSuccess.action(institutionId, childId, newSplitConfiguration),
            sensitiveInformation && successAction.payload?.billPayer?.billPayerId
                ? BillPayerActions.updateSensitiveInformation.action(
                      institutionId,
                      successAction.payload.billPayer.billPayerId,
                      sensitiveInformation,
                  )
                : null,
        ].filter(hasValue);
    });
};

const searchForBillPayer = action$ =>
    action$
        .ofType(Actions.search.type)
        .debounceTime(500)
        .mergeMap(({payload}) => {
            if (payload.term || payload.initial) {
                return Api.searchBillPayer(payload.institutionId, payload.childId, payload.term)
                    .flatMap(({billPayers, children}) =>
                        [
                            ChildActions.fetchAllSuccess.action(children),
                            Actions.searchSuccess.action({billPayers, initial: payload.initial}),

                            // Used to give suggestions for when we add bill payers in the context of a child.
                            payload.initial && hasValue(payload.childId)
                                ? Actions.setCandidateBillPayers.action(billPayers)
                                : undefined,
                        ].filter(a => a),
                    )
                    .catch(e => Observable.of(Actions.searchFailed.action(e)));
            } else {
                return Observable.of(Actions.searchAborted.action());
            }
        });

const saveBillPayer = action$ =>
    action$
        .ofType(Actions.saveBillPayer.type)
        .debounceTime(500)
        .mergeMap(({payload}) => {
            const {institutionId, billPayer, sensitiveInformation} = payload;

            return createOrUpdateObservable(institutionId, '', billPayer)
                .flatMap(successAction => {
                    return [
                        successAction,
                        Actions.saveBillPayerSuccess.action(successAction.payload.billPayer),
                        sensitiveInformation && successAction.payload?.billPayer?.billPayerId
                            ? BillPayerActions.updateSensitiveInformation.action(
                                  institutionId,
                                  successAction.payload.billPayer.billPayerId,
                                  sensitiveInformation,
                              )
                            : null,
                    ].filter(hasValue);
                })
                .catch(e => Observable.of(Actions.saveBillPayerFailed.action(e)));
        });

const discontinueBillPayer = action$ =>
    action$.ofType(Actions.discontinueBillPayer.type).mergeMap(({payload}) =>
        discontinueBillPayerObservable(payload.institutionId, payload.billPayerId)
            .flatMap(successActions => {
                return successActions.concat([Actions.discontinueBillPayerSuccess.action()]);
            })
            .catch(e => Observable.of(Actions.discontinueBillPayerFailed.action(e))),
    );

const reviveBillPayer = action$ =>
    action$.ofType(Actions.reviveBillPayer.type).mergeMap(({payload}) =>
        reviveBillPayerObservable(payload.institutionId, payload.billPayerId)
            .flatMap(successActions => {
                return successActions.concat([Actions.reviveBillPayerSuccess.action()]);
            })
            .catch(e => Observable.of(Actions.reviveBillPayerFailed.action(e))),
    );

const reviveBillPayerAndAddToChild = action$ =>
    action$.ofType(Actions.reviveBillPayerAndAddToChild.type).mergeMap(({payload}) =>
        reviveBillPayerObservable(payload.institutionId, payload.billPayerId)
            .flatMap(successActions => {
                let observable;
                const {addBillPayerToChildActionData} = payload;
                if (payload.persist) {
                    observable = addBillPayerToChildObservable(
                        addBillPayerToChildActionData.institutionId,
                        addBillPayerToChildActionData.childId,
                        addBillPayerToChildActionData.existingChildBillPayers,
                        addBillPayerToChildActionData.existingChildSplitConfiguration,
                        addBillPayerToChildActionData.newBillPayerData,
                    );
                } else {
                    observable = localAddBillPayerToChildObservable(
                        addBillPayerToChildActionData.institutionId,
                        addBillPayerToChildActionData.childId,
                        addBillPayerToChildActionData.existingChildBillPayers,
                        addBillPayerToChildActionData.existingChildSplitConfiguration,
                        addBillPayerToChildActionData.newBillPayerData,
                    );
                }
                return observable.flatMap(addBillPayerToChildActions => {
                    return successActions
                        .concat(addBillPayerToChildActions)
                        .concat([Actions.reviveBillPayerAndAddToChildSuccess.action()]);
                });
            })
            .catch(e => Observable.of(Actions.reviveBillPayerAndAddToChildFailed.action(e))),
    );

const addBillPayerToChild = action$ =>
    action$.ofType(Actions.addBillPayerToChild.type).mergeMap(({payload}) =>
        localAddBillPayerToChildObservable(
            payload.institutionId,
            payload.childId,
            payload.existingChildBillPayers,
            payload.existingChildSplitConfiguration,
            payload.newBillPayerData,
            payload.sensitiveInformation,
        )
            .flatMap(actions => actions)
            .catch(e => Observable.of(Actions.addBillPayerToChildFailed.action(e))),
    );

// Resets the "ref1" field when the user enables the autogenerated id.
const resetReferenceFieldOnAutogenerateBillPayerReferenceChange = action$ =>
    action$.pipe(
        ofType(actionTypes.CHANGE),
        filter(action => {
            const isBillPayerForm = action?.meta?.form === Constants.formName;
            const isAutoGenerateFieldName =
                action?.meta?.field ===
                `${BillPayerFormConstants.billPayerFormSectionName}.${BillPayerFormConstants.autoGenerateReferenceNumberFieldName}`;
            const didEnable = Boolean(action?.payload);

            return isBillPayerForm && isAutoGenerateFieldName && didEnable;
        }),
        flatMap(() => {
            return Observable.of(
                change(
                    Constants.formName,
                    `${BillPayerFormConstants.billPayerFormSectionName}.${BillPayerFormConstants.referencesSectionName}.${BillPayerFormConstants.ref1FieldName}`,
                    null,
                ),
            );
        }),
    );

export default combineEpics(
    reviveBillPayerAndAddToChild,
    searchForBillPayer,
    addBillPayerToChild,
    discontinueBillPayer,
    reviveBillPayer,
    saveBillPayer,
    resetReferenceFieldOnAutogenerateBillPayerReferenceChange,
);
